import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { apiClient } from "../lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/blog").then((r) => {
      setPosts(r.data);
      setLoading(false);
    });
  }, []);

  return (
    <div data-testid="blog-list-page" className="flex flex-1 flex-col gap-6 md:gap-8">
      <section className="space-y-3 md:space-y-4">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Blog</p>
        <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">
          Our work and journey.
        </h1>
        <p className="max-w-prose text-sm text-muted-foreground md:text-base">
          Read more about our work and the journey of developing DPI governance e-learning courses.
        </p>
      </section>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : posts.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="blog-empty">No posts yet. Check back soon.</p>
      ) : (
        <div className="grid gap-4">
          {posts.map((p) => (
            <Card key={p.slug} className="card-elevated" data-testid={`blog-card-${p.slug}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#361a54]">
                  <Link to={`/blog/${p.slug}`} className="hover:underline">{p.title}</Link>
                </CardTitle>
                <CardDescription className="text-xs">
                  {p.author_name || "DPI Applied Learning team"} · {formatDate(p.published_at)}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-slate-700">{p.excerpt}</CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    apiClient
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setErr("Post not found"));
  }, [slug]);

  if (err) return <p className="py-10 text-center text-sm text-muted-foreground" data-testid="blog-not-found">{err}</p>;
  if (!post) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  return (
    <article data-testid="blog-post-page" className="mx-auto w-full max-w-3xl space-y-4 py-6">
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Blog</p>
      <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">{post.title}</h1>
      <p className="text-xs text-muted-foreground">
        {post.author_name || "DPI Applied Learning team"} · {formatDate(post.published_at)}
      </p>
      <div
        className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed text-slate-700"
        data-testid="blog-post-body"
      >
        {post.body}
      </div>
      <p>
        <Link to="/blog" className="text-sm text-[#1a8bb3] hover:underline">← Back to all posts</Link>
      </p>
    </article>
  );
}
